import { useRef } from 'react';
import styled from '@emotion/styled';
import { HEATMAP_WIDTH_PER_DEVICE, Layer } from '@hotjar/heatmaps';
import { ClickRegion } from '@hotjar/webapp/features/Heatmaps/ContinuousCaptureReport/ClickRegions/ClickRegion';
import { useWidth } from '@hotjar/insights-common';

import type { ClickRegionPosition, ClickRegionWithZIndex } from './helpers';
import { includeZIndexToRegions } from './helpers';

type ClickRegionContainerProps = {
  width: number;
  height: number;
  zoom: number;
};

const ClickRegionContainer = Layer.withComponent(styled.div<ClickRegionContainerProps>`
  position: relative;
  width: 100%;

  ${({ height, zoom }) => `
    max-height: ${height}px;
    zoom: ${zoom};
  `}
`);

export interface ClickRegionsProps {
  clickRegions: Array<HeatmapAPI.ClickRegion>;
  count: number;
  selectedDeviceType: HeatmapDeviceTypes;
  width: number;
  height: number;
  onRegionClick: (position: ClickRegionPosition) => void;
  topClickedElements: HeatmapAPI.TopClickedElement[];
}

function getMatchingTopClickedElement(
  topClickedElements: HeatmapAPI.TopClickedElement[],
  clickRegion: ClickRegionWithZIndex,
): number | null {
  if (!topClickedElements || topClickedElements.length === 0) {
    return null;
  }

  let matchingTopClickedElement: number | null = null;

  topClickedElements.some((el, index) => {
    if (
      el[1] === clickRegion[0] &&
      el[2] === clickRegion[1] &&
      el[3] === clickRegion[2] &&
      el[4] === clickRegion[3] &&
      el[5] === clickRegion[4]
    ) {
      matchingTopClickedElement = index + 1;
      return true;
    }
    return false;
  });

  return matchingTopClickedElement;
}

export const ClickRegions = ({
  clickRegions,
  count,
  selectedDeviceType,
  width,
  height,
  onRegionClick,
  topClickedElements,
}: ClickRegionsProps): JSX.Element => {
  // Certain screenshots may have been taken larger than they should due to elements
  // not fitting in the specified widths. This causes click regions positions to not match
  // when placed over the screenshot. To fix this we create a zoom float which will fit them
  // properly.
  const zoom = HEATMAP_WIDTH_PER_DEVICE[selectedDeviceType] / width;
  // We want to avoid to have small regions hidden under larger ones, so
  // we want to adjust click regions and apply to them a zIndex value based
  // on their size.
  const adjustedClickRegions = includeZIndexToRegions(clickRegions);

  const ref = useRef<HTMLDivElement>(null);
  const [actualWidth] = useWidth(ref, width);

  return (
    <ClickRegionContainer zoom={zoom} width={width} height={height} ref={ref}>
      {adjustedClickRegions.map((clickRegion) => {
        const matchingTopClickedElement = getMatchingTopClickedElement(
          topClickedElements,
          clickRegion,
        );

        return (
          <ClickRegion
            key={clickRegion[5]}
            clickRegion={clickRegion}
            selectedDeviceType={selectedDeviceType}
            totalCount={count}
            heatmapWidth={width}
            actualWidth={actualWidth}
            onRegionClick={onRegionClick}
            matchingTopClickedElement={matchingTopClickedElement}
          />
        );
      })}
    </ClickRegionContainer>
  );
};
